import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useAuth } from './useAuth';
import {
  guestEntitlements,
  loadEntitlements,
  type Entitlements,
  type EntitlementsLoad,
  type MarketToolsEntitlement,
} from '../services/entitlementsService';
import { isProfessional as isProfessionalSession } from '../utils/isProfessional';

/**
 * useEntitlements: what this viewer may use, from GET /api/entitlements.
 *
 * One module-level store, read through useSyncExternalStore, so no provider is
 * needed (App.tsx is untouched) and every caller (the sidebar, the market
 * console, ProfessionalRoute, DemoBanner) shares one answer and one request.
 *
 * - Keyed by viewer: 'guest', or the signed-in user's id. A token refresh keeps
 *   the cached answer; signing in or out switches to another entry.
 * - Fetches only while `enabled` (default true) and once the session has been
 *   restored. Concurrent callers share the in-flight request.
 * - On mount, an answer older than 60 s (the server's own cache window) is
 *   revalidated in the background; the old answer stays on screen meanwhile.
 * - Never throws. A failed first load yields the guest-safe fallback with
 *   `error: true`; call `refresh()` to retry. A failed revalidation keeps the
 *   last good answer.
 * - invalidateEntitlements() drops every answer. Call it after a purchase: the
 *   next loads also bypass the server cache.
 */

const STALE_AFTER_MS = 60_000;
const FORCE_FRESH_WINDOW_MS = 60_000;

interface Entry {
  data: Entitlements | null;
  error: boolean;
  /** 0 until the first answer for this viewer arrives. */
  fetchedAt: number;
}

interface Inflight {
  id: number;
  fresh: boolean;
  promise: Promise<void>;
}

function createEmptyEntry(): Entry {
  return Object.freeze({ data: null, error: false, fetchedAt: 0 });
}

const entries = new Map<string, Entry>();
const inflight = new Map<string, Inflight>();
const listeners = new Set<() => void>();
// Swapped on every invalidation, so a reader whose entry was dropped sees a new
// snapshot (and re-runs its load effect) even if it was already waiting.
let emptyEntry = createEmptyEntry();
let requestSeq = 0;
let forceFreshUntil = 0;

function emit(): void {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function readEntry(key: string): Entry {
  return entries.get(key) ?? emptyEntry;
}

function load(key: string, token: string | null, fresh: boolean): Promise<void> {
  const wantFresh = fresh || Date.now() < forceFreshUntil;
  const current = inflight.get(key);
  // Share the request in flight unless this call needs a fresher answer than it.
  if (current && (current.fresh || !wantFresh)) return current.promise;

  const id = ++requestSeq;
  const promise = loadEntitlements(token, { fresh: wantFresh })
    // loadEntitlements resolves even on failure; this only guards against a bug
    // leaving the request (and every reader) pending for good.
    .catch((): EntitlementsLoad => ({ ok: false, data: guestEntitlements(Boolean(token)) }))
    .then(({ data, ok }) => {
      // Superseded by a newer request, or dropped by invalidateEntitlements().
      if (inflight.get(key)?.id !== id) return;
      inflight.delete(key);
      const previous = readEntry(key);
      // A failed revalidation must not downgrade a viewer who had a real answer.
      if (!ok && previous.data && !previous.error) return;
      entries.set(key, { data, error: !ok, fetchedAt: Date.now() });
      emit();
    });
  inflight.set(key, { id, fresh: wantFresh, promise });
  return promise;
}

/**
 * Forget every cached answer, so each reader loads again. After a purchase the
 * server's 60-second cache is stale too, so for the next minute every load asks
 * it to refresh (`?fresh=1`); pass `{ refreshServerCache: false }` to skip that
 * (for example on sign-out).
 */
export function invalidateEntitlements(options: { refreshServerCache?: boolean } = {}): void {
  const { refreshServerCache = true } = options;
  forceFreshUntil = refreshServerCache ? Date.now() + FORCE_FRESH_WINDOW_MS : 0;
  inflight.clear();
  entries.clear();
  emptyEntry = createEmptyEntry();
  emit();
}

export interface UseEntitlementsOptions {
  /** Load from the server. False reads only what is already cached. Default true. */
  enabled?: boolean;
}

export interface UseEntitlementsResult {
  /** True while an enabled caller waits for its first answer (or for the session to restore). */
  loading: boolean;
  /** True when the current answer is the guest-safe fallback because the request failed. */
  error: boolean;
  data: Entitlements | null;
  /** The session check (utils/isProfessional) OR the server's answer. */
  isProfessional: boolean;
  market: MarketToolsEntitlement | null;
  /** Reload now, bypassing the server cache. Resolves once the answer is in. */
  refresh: () => Promise<void>;
}

export function useEntitlements({ enabled = true }: UseEntitlementsOptions = {}): UseEntitlementsResult {
  const { user, session, loading: authLoading } = useAuth();
  const token = session?.token ?? null;
  const key = token && user?.id ? `u:${user.id}` : 'guest';

  const entry = useSyncExternalStore(
    subscribe,
    () => readEntry(key),
    () => readEntry(key)
  );

  const active = enabled && !authLoading;

  // Re-runs whenever this viewer's entry changes, including when
  // invalidateEntitlements() drops it; the staleness check keeps a fresh answer
  // from being fetched again.
  useEffect(() => {
    if (!active) return;
    if (entry.fetchedAt === 0 || Date.now() - entry.fetchedAt > STALE_AFTER_MS) {
      void load(key, token, false);
    }
  }, [active, key, token, entry]);

  const refresh = useCallback(() => load(key, token, true), [key, token]);

  const sessionPro = isProfessionalSession(user);
  const loading = enabled && (authLoading || entry.fetchedAt === 0);

  return useMemo(
    () => ({
      loading,
      error: entry.error,
      data: entry.data,
      isProfessional: sessionPro || entry.data?.professional.value === true,
      market: entry.data?.marketTools ?? null,
      refresh,
    }),
    [loading, entry, sessionPro, refresh]
  );
}
